import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import { map, isArray, find, last, get } from 'lodash';
import moment from 'moment';

import AvatarIcon from 'components/AvatarIcon';

import TimelineLabel from './TimelineLabel';

const styles = (theme) => {
  const iconRadius = 18;
  const iconTop = theme.spacing.unit * 5 + theme.spacing.unit / 2;
  const cardMargin = 120;
  const cardMarginSM = cardMargin / 4;
  const border = `2px solid ${theme.custom.palette.colorSupportSteel}`;

  return {
    headerIcon: {
      margin: `${theme.spacing.unit}px 0`,
      '& > span': {
        color: 'white',
      },
    },
    timelineLabel: {
      margin: `${theme.spacing.unit}px 0`,
      pointerEvents: 'none',
    },
    subheaderLine: {
      borderTop: `2px solid ${theme.custom.palette.colorSupportSteel}`,
      width: theme.spacing.unit * 3,
      margin: 0,
    },
    subheaderLineDashed: {
      borderTopStyle: 'dashed !important',
    },
    subheaderVerticalLine: {
      height: theme.spacing.unit * 8,
      borderLeft: `2px solid ${theme.custom.palette.colorSupportSteel}`,
      position: 'relative',
      top: 31,
    },
    subheaderVerticalLineDashed: {
      borderLeftStyle: 'dashed !important',
    },
    singlelineHeaderLabel: {
      marginLeft: theme.spacing.unit * 2,
    },
    singlelineHeaderLastEvent: {
      marginLeft: theme.spacing.unit * 5,
    },
    singlelineMonthLabel: {
      paddingLeft: 0,
      marginLeft: 0,
    },
    gapSolid: {
      height: 20,
      position: 'relative',
      borderRight: border,
      borderLeft: 'none !important',
    },
    gapSingleline: {
      height: theme.spacing.unit * 3,
      position: 'relative',
      borderLeft: border,
      borderRight: 'none !important',
    },
    gapLong: {
      height: 40,
    },
    gapWithMargin: {
      margin: `${theme.spacing.unit}px 0 !important`,
    },
    dashedRight: {
      borderRightStyle: 'dashed !important',
      marginTop: 2,
      marginBottom: 2,
    },
    dashedLeft: {
      borderLeftStyle: 'dashed !important',
      marginTop: theme.spacing.unit / 2,
    },
    dashedTop: {
      borderTopStyle: 'dashed !important',
    },
    cardContainer: {
      width: '100%',
      position: 'relative',
    },
    cardContainerLeft: {
      paddingRight: cardMargin,
      borderRight: border,
      [theme.breakpoints.down('sm')]: {
        paddingRight: cardMarginSM,
      },
    },
    cardContainerRight: {
      paddingLeft: cardMargin,
      borderLeft: border,
      marginLeft: -2,
      [theme.breakpoints.down('sm')]: {
        paddingLeft: cardMarginSM,
      },
    },
    cardContainerSingleline: {
      paddingLeft: theme.spacing.unit * 5,
      borderLeft: border,
      [theme.breakpoints.down('sm')]: {
        paddingLeft: cardMarginSM,
      },
    },
    cardDecorator: {
      position: 'absolute',
      top: iconTop,
      width: cardMargin,
      borderTop: border,
      [theme.breakpoints.down('sm')]: {
        width: cardMarginSM,
      },
    },
    cardDecoratorLeft: {
      right: 0,
    },
    cardDecoratorRight: {
      left: 0,
    },
    cardDecoratorSingleline: {
      left: 0,
      width: theme.spacing.unit * 5,
    },
    cardDecoratorIcon: {
      color: 'white',
      width: iconRadius * 2,
      height: iconRadius * 2,
      position: 'absolute',
      top: iconTop - iconRadius,
      '& span': {
        fontSize: iconRadius + 2,
      },
    },
    cardDecoratorIconLeft: {
      right: cardMargin - iconRadius,
      [theme.breakpoints.down('sm')]: {
        right: cardMarginSM - iconRadius,
      },
    },
    cardDecoratorIconRight: {
      left: cardMargin - iconRadius,
      [theme.breakpoints.down('sm')]: {
        left: cardMarginSM - iconRadius,
      },
    },
    cardDecoratorIconSingleline: {
      left: theme.spacing.unit * 3,
    },
  };
};

class Timeline extends Component {
  eventRefs = [];

  numEventsRendered = 0;

  componentWillUpdate(nextProps) {
    const { events } = this.props;
    this.numEventsRendered = 0;
    if (events !== nextProps.events) {
      this.eventRefs = [];
    }
  }

  componentDidUpdate(prevProps) {
    const { date, events } = this.props;
    this.numEventsRendered = 0;
    if (date && (date !== prevProps.date || (events && events !== prevProps.events))) {
      setTimeout(() => {
        this.handleScroll(date);
      }, 0);
    }
  }

  handleScroll = (value) => {
    // get ref to the event matching the date selected
    const dateValue = moment.unix(value);
    const matchingEvent = find(this.eventRefs, ({ date }) => date && !date.isBefore(dateValue));
    const node = get(matchingEvent || last(this.eventRefs), 'ref');
    if (node) {
      // scroll to the event
      node.scrollIntoView({ behavior: 'smooth' });
    }
  };

  render() {
    const { singleline } = this.props;
    return (
      <div>
        {this.renderHeader()}
        <Grid container>
          {this.renderGap()}
          {!singleline && (
            <>
              {this.renderGap(true, true, true)}
              {this.renderGap()}
            </>
          )}
          {this.renderEvents()}
        </Grid>
      </div>
    );
  }

  renderHeader() {
    const { classes, headerDate: date } = this.props;

    return this.renderSubheader({
      icon: (
        <Grid item>
          <AvatarIcon className={classes.headerIcon}>cake</AvatarIcon>
        </Grid>
      ),
      label: date.format('LL'),
      date,
      buttonProps: {
        variant: 'outlined',
      },
    });
  }

  renderEvents = () => {
    const { events } = this.props;
    if (!isArray(events)) {
      return map(events, this.renderYear);
    }
    return map(events, this.renderRow);
  };

  renderYear = (months, year) => {
    const { singleline } = this.props;
    return (
      <Grid key={year} container>
        {!singleline &&
          this.renderSubheader({
            label: year,
            date: moment()
              .year(year)
              .startOf('year'),
            buttonProps: {
              variant: 'contained',
              color: 'primary',
            },
          })}
        {this.renderGap()}
        {map(months, (events, month) => {
          const date = moment()
            .year(year)
            .month(month)
            .startOf('month');
          const isPast = moment().isSameOrBefore(date);
          const displayEmptyMonth = events[0].type !== 'today' && singleline;
          return (
            <Grid key={month} container>
              {displayEmptyMonth &&
                this.renderSubheader({
                  label: date.format('MMMM YYYY'),
                  date,
                })}
              {displayEmptyMonth && this.renderGap(isPast)}
              {map(events, this.renderRow)}
            </Grid>
          );
        })}
      </Grid>
    );
  };

  renderSubheader({ icon, lastEvent = false, dashed, label, date, buttonProps = {} }) {
    const { classes, singleline } = this.props;

    const gridAttributes = {
      direction: singleline ? 'row' : 'column',
      justify: singleline ? 'flex-start' : 'center',
    };
    const displaySinglelineBorder = singleline && buttonProps.variant === 'outlined' && !lastEvent;
    return (
      <Grid
        key={label}
        className={classes.subheader}
        container
        direction={gridAttributes.direction}
        justify={gridAttributes.justify}
        alignItems="center"
      >
        {displaySinglelineBorder && (
          <>
            <div
              className={classnames(classes.subheaderVerticalLine, {
                [classes.subheaderVerticalLineDashed]: dashed,
              })}
            />
            <hr
              className={classnames(classes.subheaderLine, {
                [classes.subheaderLineDashed]: dashed,
              })}
            />
          </>
        )}
        {icon}
        <Grid item>
          <div
            ref={(ref) => {
              this.eventRefs.push({ ref, date });
            }}
          >
            <TimelineLabel
              className={classnames(classes.timelineLabel, {
                [classes.singlelineHeaderLabel]: singleline && !lastEvent,
                [classes.singlelineHeaderLastEvent]: singleline && lastEvent,
                [classes.singlelineMonthLabel]: singleline && buttonProps.variant !== 'outlined',
              })}
              size="large"
              {...buttonProps}
            >
              {label}
            </TimelineLabel>
          </div>
        </Grid>
      </Grid>
    );
  }

  renderGap = (dashed, long, withMargin) => {
    const { classes, singleline } = this.props;
    const uid = `_${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    if (singleline) {
      return (
        <Grid
          key={uid}
          item
          xs={1}
          className={classnames(classes.gapSingleline, {
            [classes.dashedLeft]: dashed,
            [classes.gapLong]: long,
            [classes.gapWithMargin]: withMargin,
          })}
        />
      );
    }

    return [
      <Grid
        key="gapSolidLeft"
        item
        xs={6}
        className={classnames(classes.gapSolid, {
          [classes.dashedRight]: dashed,
          [classes.gapLong]: long,
          [classes.gapWithMargin]: withMargin,
        })}
      />,
      <Grid key="gapSolidRight" item xs={6} />,
    ];
  };

  renderRow = (event) => {
    const { singleline } = this.props;
    if (event.type === 'today') {
      const label = `Today, ${event.date.format('LL')}`;
      return [
        this.renderGap(false, true),
        this.renderSubheader({
          label,
          date: event.date,
          buttonProps: {
            variant: 'outlined',
          },
          dashed: true,
          lastEvent: event.isLast,
        }),
        !event.isLast && this.renderGap(true, true),
      ];
    }
    const isEven = this.numEventsRendered % 2 === 0;
    this.numEventsRendered += 1;

    if (singleline) {
      return (
        <Grid item xs={12} key={`right-${this.numEventsRendered}`}>
          {this.renderEvent(event, false)}
          {this.renderGap(event.isFuture)}
        </Grid>
      );
    }

    return [
      <Grid item xs={6} key={`left-${this.numEventsRendered}`}>
        {isEven && this.renderEvent(event, true)}
      </Grid>,
      <Grid item xs={6} key={`right-${this.numEventsRendered}`}>
        {!isEven && this.renderEvent(event, false)}
      </Grid>,
    ];
  };

  renderEvent = (event, isLeft) => {
    const { classes, handleRenderEventContent, singleline } = this.props;

    return (
      <div
        ref={(ref) => {
          this.eventRefs.push({ ref, date: event.date });
        }}
        className={classnames(classes.cardContainer, {
          [classes.cardContainerLeft]: isLeft,
          [classes.cardContainerRight]: !isLeft && !singleline,
          [classes.cardContainerSingleline]: singleline,
          [classes.dashedRight]: event.isFuture && isLeft,
          [classes.dashedLeft]: event.isFuture && !isLeft,
        })}
      >
        <div
          className={classnames(classes.cardDecorator, {
            [classes.cardDecoratorLeft]: isLeft,
            [classes.cardDecoratorRight]: !isLeft && !singleline,
            [classes.cardDecoratorSingleline]: singleline,
            [classes.dashedTop]: event.isFuture,
          })}
        />
        {this.renderIcon(event, isLeft)}
        {handleRenderEventContent(event, isLeft)}
      </div>
    );
  };

  renderIcon(event, isLeft) {
    const { classes, singleline } = this.props;
    return (
      <AvatarIcon
        className={classnames(classes.cardDecoratorIcon, {
          [classes.cardDecoratorIconLeft]: isLeft,
          [classes.cardDecoratorIconRight]: !isLeft && !singleline,
          [classes.cardDecoratorIconSingleline]: singleline,
        })}
      >
        {event.type}
      </AvatarIcon>
    );
  }
}

Timeline.propTypes = {
  classes: PropTypes.object.isRequired,
  events: PropTypes.oneOfType([PropTypes.array, PropTypes.object, PropTypes.bool]),
  date: PropTypes.string,
  headerDate: PropTypes.object,
  handleRenderEventContent: PropTypes.func.isRequired,
};

export default withStyles(styles)(Timeline);
